import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { EasterEggs } from "abreact/app/easterEggs/EasterEggs";
import { AbThemingExtensionPoints } from "abreact/app/theming/AbThemingExtensionPoints";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbReactServices } from "abreact/service/AbReactServices";
import { AbSet } from "abweb/collection/AbSet";
import { ProductCapability } from "abweb/dto/product/ProductCapability";
import { AbWebServices } from "abweb/service/AbWebServices";
import { LogUtils } from "abweb/utils/log/LogUtils";
const LOG = LogUtils.getLogger(__filename);
export class AbDefaultThemingService {
  constructor(options = {}) {
    _defineProperty(this, "_fallbackThemeId", void 0);
    _defineProperty(this, "_currentThemeId", void 0);
    _defineProperty(this, "_currentThemeTweakClassnames", void 0);
    this._fallbackThemeId = options.classicThemeOnly ? "abClassicTheme" : "abLightTheme";
    if (options.classicThemeOnly) {
      AbThemingExtensionPoints.applicationThemes.registry.register(() => new AbClassicAppTheme());
    } else {
      // Ideally if we're in dev mode we would also register classic theme here
      // But we can't call AbWebServices.isDevelopmentImage() from here

      AbThemingExtensionPoints.applicationThemes.registry.register(() => new AbLightAppTheme());
      AbThemingExtensionPoints.applicationThemes.registry.register(() => new AbDarkAppTheme());

      // Register Easter eggs for each theme tweak
      this.getAllThemeTweaks().forEach(themeTweak => {
        EasterEggs.registry.register(() => new ThemeTweakEasterEgg(themeTweak));
      });
    }
    const defaultThemeTweakClassnames = options.defaultThemeTweakClassnames ?? [];
    try {
      this._currentThemeId = window.localStorage.getItem(AbDefaultThemingService.THEME_LOCAL_STORAGE_ID) ?? this._fallbackThemeId;
      this._currentThemeTweakClassnames = window.localStorage.getItem(AbDefaultThemingService.THEME_TWEAKS_LOCAL_STORAGE_ID)?.split(",") ?? defaultThemeTweakClassnames;
    } catch {
      this._currentThemeId = this._fallbackThemeId;
      this._currentThemeTweakClassnames = defaultThemeTweakClassnames;
    }
  }
  getCurrentTheme() {
    const theme = AbThemingExtensionPoints.applicationThemes.get(this._currentThemeId);
    if (!theme) {
      LOG.warn(`Unable to find theme ${this._currentThemeId} so falling back to ${this._fallbackThemeId}`);
      const fallbackTheme = AbThemingExtensionPoints.applicationThemes.get(this._fallbackThemeId);
      if (!fallbackTheme) {
        throw new Error(`Unable to load either theme ${this._currentThemeId} or fallback "${this._fallbackThemeId}"`);
      }
      return fallbackTheme;
    }
    return theme;
  }
  setCurrentTheme(themeIdentifier) {
    const themeId = typeof themeIdentifier === "string" ? themeIdentifier : themeIdentifier.id;
    try {
      window.localStorage.setItem(AbDefaultThemingService.THEME_LOCAL_STORAGE_ID, themeId);
    } catch {
      // do nothing
    }
    this._currentThemeId = themeId;
  }
  getCurrentThemeTweaks() {
    const themeTweaks = this._currentThemeTweakClassnames.map(className => {
      const tweak = AbThemingExtensionPoints.applicationThemeTweaks.get(className);
      if (!tweak) {
        LOG.warn(`Unable to find theme tweak ${className}`);
      }
      return tweak;
    }).filter(tweak => !!tweak);
    return AbSet.from(themeTweaks);
  }
  setCurrentThemeTweaks(newThemeTweaks) {
    const themeTweaks = Array.from(newThemeTweaks);
    try {
      window.localStorage.setItem(AbDefaultThemingService.THEME_TWEAKS_LOCAL_STORAGE_ID, themeTweaks.map(tweak => tweak.themeClassName).join(","));
    } catch {
      // do nothing
    }
    this._currentThemeTweakClassnames = themeTweaks.map(tweak => tweak.themeClassName);
  }
  getAllThemes() {
    if (AbWebServices.sessionData.productInfo.hasCapability(ProductCapability.GA2_EXPERIENCE)) {
      return Array.from(AbThemingExtensionPoints.applicationThemes.get().values());
    } else {
      return [this.getCurrentTheme()];
    }
  }
  getAllThemeTweaks() {
    return Array.from(AbThemingExtensionPoints.applicationThemeTweaks.get().values());
  }

  /**
   * Initializes the theming service to use either the user's preferred theme, or the default.
   */
  async initServices() {
    const theme = this.getCurrentTheme();
    const themeTweaks = this.getCurrentThemeTweaks();

    // Import any CSS from the current theme so that it will be added to the page before it renders
    await theme.initialize();

    // Add the theme class to the body element of the document
    document.documentElement.classList.add(theme.themeClassName);
    themeTweaks.forEach(tweak => document.documentElement.classList.add(tweak.themeClassName));
  }
}
_defineProperty(AbDefaultThemingService, "THEME_LOCAL_STORAGE_ID", "abChosenTheme");
_defineProperty(AbDefaultThemingService, "THEME_TWEAKS_LOCAL_STORAGE_ID", "abChosenThemeTweaks");
class AbClassicAppTheme {
  constructor() {
    _defineProperty(this, "id", "abClassicTheme");
    _defineProperty(this, "displayName", AbReactResources.theming_classicThemeDisplayName());
    _defineProperty(this, "themeClassName", "ab-classic-theme");
  }
  async initialize() {
    // No additional initialization is needed as the CSS is already loaded
    return;
  }
}
class AbLightAppTheme {
  constructor() {
    _defineProperty(this, "id", "abLightTheme");
    _defineProperty(this, "themeClassName", "ab-light-mode");
  }
  async initialize() {
    // No additional initialization is needed as the CSS is already loaded
    return;
  }
  get displayName() {
    return AbReactResources.theming_lightThemeDisplayName();
  }
}
class AbDarkAppTheme {
  constructor() {
    _defineProperty(this, "id", "abDarkTheme");
    _defineProperty(this, "themeClassName", "ab-dark-mode");
    _defineProperty(this, "isDarkTheme", true);
  }
  async initialize() {
    // No additional initialization is needed as the CSS is already loaded
    return;
  }
  get displayName() {
    return AbReactResources.theming_darkThemeDisplayName();
  }
}
class ThemeTweakEasterEgg {
  constructor(themeTweak) {
    _defineProperty(this, "themeTweak", void 0);
    this.themeTweak = themeTweak;
  }
  get displayName() {
    return this.themeTweak.displayName;
  }
  get description() {
    // TODO AndyA: set a description for the theme tweak
    return undefined;
  }
  isAvailable() {
    return true;
  }
  isEnabled() {
    return AbReactServices.themingService.getCurrentThemeTweaks().has(this.themeTweak);
  }
  setEnabled(enabled) {
    const updatedThemeTweaks = AbReactServices.themingService.getCurrentThemeTweaks();
    if (enabled) {
      updatedThemeTweaks.add(this.themeTweak);
    } else {
      updatedThemeTweaks.delete(this.themeTweak);
    }
    AbReactServices.themingService.setCurrentThemeTweaks(updatedThemeTweaks);
  }
}
//# sourceMappingURL=AbDefaultThemingService.js.map
